import moment from "moment";
import { hasValue } from "~/helpers/common-helpers";
import { getUpcomingTenant, type ExternalEntity, type Tenancy } from "~/helpers/entities/tenancy-helpers";
import type { TenantStatus } from "~/helpers/modules/rent-roll-helpers";

export type ArrearsBalance = {
  balance?: Nullable<number>;
  currency?: Nullable<string>;
};

export type Tenant = {
  id: string;
  name?: Nullable<string>;
  currency?: Nullable<string>;
  moveInDate?: Nullable<string>;
  evictionDate?: Nullable<string>;
  latestPlannedRentMetric?: Nullable<RentMetric>;
  balanceAt?: Nullable<ArrearsBalance>;
  latestBalance?: Nullable<ArrearsBalance>;
  irrevocableUntilLandlord?: Nullable<string>;
  irrevocableUntilTenant?: Nullable<string>;
  isGhost?: Nullable<boolean>;
  regulation?: Nullable<string>;
} & ExternalEntity;

export type RentMetric = {
  currency?: Nullable<string>;
  amount?: Nullable<number>;
};
export type Tenants = Tenant[];

export const isTenantCurrent = (tenant: Tenant) => {
  if (!tenant) return false;

  const today = moment();
  const moveInDate = moment(tenant.moveInDate);
  const evictionDate = moment(tenant.evictionDate);

  if (moveInDate.isAfter(today)) return false;
  if (evictionDate.isBefore(today)) return false;

  return true;
};

export const getRemainingTenants = (currentTenant: Nullable<Tenant>, tenants: Tenants) => {
  if (tenants == null || tenants.length === 0) return [];

  const sorted = [...tenants].sort((a, b) => {
    return isUnikId(getExternalIdForTenant(a)) && isUnikId(getExternalIdForTenant(b)) // Special case for UNIK data as we can use their Id to determine the hostory
      ? unikIdSort(a, b)
      : moment(b.moveInDate).diff(moment(a.moveInDate));
  });

  if (currentTenant == null) return sorted;

  return sorted.filter((tenant) => {
    return tenant.id !== currentTenant.id;
  });
};

export const getExternalIdForTenant = (tenant: ExternalEntity) => {
  const externalId = tenant.assetManagementExternalIDs?.items.find((item) => {
    return item.externalIDType == "id";
  });

  return externalId ? externalId.externalID : null;
};

export const isUnikId = (id: string | null) => {
  if (id === null) return false;

  const regex = new RegExp("([0-9]*[-]){3}[0-9*]");

  return regex.test(id);
};

export const unikIdSort = function (a: Tenant, b: Tenant) {
  const aId = getExternalIdForTenant(a);
  const bId = getExternalIdForTenant(b);

  if (aId == null || bId == null) return 0;

  const tentantNumberA = parseInt(aId.split("-")[3]);
  const tentantNumberB = parseInt(bId.split("-")[3]);

  return tentantNumberB - tentantNumberA;
};

export const getYearlyRentForTenant = function (tenant: Tenant) {
  return tenant?.latestPlannedRentMetric?.amount || 0;
};

export const getMonthlyRentForTenant = function (tenant: Tenant) {
  const yearly = getYearlyRentForTenant(tenant);

  return hasValue(yearly) ? yearly / 12 : 0;
};

export const getTenantStatus = (row: Tenancy): TenantStatus => (!!hasValue(getUpcomingTenant(row)) ? "upcomingTenant" : "noUpcomingTenant");


export type TimelineSegment = {
  type: "occupied" | "vacant";
  startDate: Date;
  endDate: Date | null;
  tenant?: any;
  left: number;
  width: number;
};

export type OccupancyStats = {
  occupiedMonths: number;
  vacantMonths: number;
  avgOccupancyPeriod: number;
  avgVacancyPeriod: number;
  avgChurnsPerYear: string;
  totalTenants: number;
};

export const calculateOccupancyStats = (timelineSegments: TimelineSegment[]): OccupancyStats => {
  const occupied = timelineSegments.filter((s) => s.type === "occupied");
  const vacant = timelineSegments.filter((s) => s.type === "vacant");

  const calculateMonths = (segments: TimelineSegment[]) => {
    return segments.reduce((total, segment) => {
      const start = segment.startDate;
      const end = segment.endDate || new Date();
      const months = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
      return total + months;
    }, 0);
  };

  const occupiedMonths = calculateMonths(occupied);
  const vacantMonths = calculateMonths(vacant);
  const totalMonths = occupiedMonths + vacantMonths;
  const totalYears = totalMonths / 12;

  // Count tenant move-outs (occupied segments that have an end date)
  const moveOuts = occupied.filter((segment) => segment.endDate !== null).length;

  return {
    occupiedMonths: Math.round(occupiedMonths),
    vacantMonths: Math.round(vacantMonths),
    avgOccupancyPeriod: occupied.length > 0 ? Math.round(occupiedMonths / occupied.length) : 0,
    avgVacancyPeriod: vacant.length > 0 ? Math.round(vacantMonths / vacant.length) : 0,
    avgChurnsPerYear: totalYears > 0 ? (moveOuts / totalYears).toFixed(2) : "0.0",
    totalTenants: occupied.length,
  };
};
